import {
    BOLD_ITALIC_STAR,
    BOLD_ITALIC_UNDERSCORE,
    BOLD_STAR,
    BOLD_UNDERSCORE,
    ITALIC_STAR,
    ITALIC_UNDERSCORE,
    LINK,
    ORDERED_LIST,
    STRIKETHROUGH,
    UNORDERED_LIST,
} from '@lexical/markdown';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import {
    $createParagraphNode,
    $getRoot,
    type EditorThemeClasses,
    type KlassConstructor,
    type LexicalEditor,
    type LexicalNode,
    type SerializedEditorState,
} from 'lexical';
import { XIcon } from 'lucide-react';
import type { MouseEvent, ReactElement, ReactNode, Ref } from 'react';
import { cn } from '../util/classes.ts';
import {
    AutoFocusPlugin,
    EditableSyncPlugin,
    internalRichTextInputStateUpdateTag,
    ValueSyncPlugin,
} from './rich-text-plugins.tsx';
import { RichTextFormattingProvider } from './rich-text-toolbar.tsx';

export type RichTextValue = SerializedEditorState;

// Clears the content itself rather than leaving it to `onClear`: a caller cannot
// do it by setting `value` back to `undefined`, since ValueSyncPlugin ignores an
// absent value so typing is never stomped by a stale prop.
function ClearButton({ label, onClear }: { label?: string; onClear?: () => void }): ReactElement {
    const [editor] = useLexicalComposerContext();

    return (
        <button
            type="button"
            aria-label={label}
            onClick={() => {
                editor.update(() => {
                    const root = $getRoot();
                    root.clear();
                    // A root with no children is not a valid document.
                    root.append($createParagraphNode());
                });
                editor.focus();
                onClear?.();
            }}
            className={cn(
                'absolute top-tight right-tight z-10 inline-flex shrink-0 cursor-pointer',
                'size-6 items-center justify-center rounded-tight',
                'text-content-weaker hover:bg-content/(--opacity-wash) hover:text-content',
                'transition duration-state ease-state active:translate-px',
            )}
        >
            <XIcon className="size-3.5" />
        </button>
    );
}

const DEFAULT_TRANSFORMERS = [
    UNORDERED_LIST,
    ORDERED_LIST,
    BOLD_ITALIC_STAR,
    BOLD_ITALIC_UNDERSCORE,
    BOLD_STAR,
    BOLD_UNDERSCORE,
    ITALIC_STAR,
    ITALIC_UNDERSCORE,
    STRIKETHROUGH,
    LINK,
];

const URL_MATCHER =
    /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

const DEFAULT_LINK_MATCHERS = [
    (text: string) => {
        const match = URL_MATCHER.exec(text);
        if (match === null) {
            return null;
        }
        const fullMatch = match[0];
        return {
            index: match.index,
            length: fullMatch.length,
            text: fullMatch,
            url: fullMatch.startsWith('http') ? fullMatch : `https://${fullMatch}`,
            attributes: { rel: 'noreferrer', target: '_blank' },
        };
    },
];

export type RichTextInputProps = {
    value: RichTextValue | undefined;
    onChange?: (value: RichTextValue) => void;
    placeholder?: string;

    // Lexical config supplied by the consumer so this component stays design-system-agnostic.
    theme: EditorThemeClasses;
    nodes: KlassConstructor<typeof LexicalNode>[];
    namespace?: string;
    onError?: (error: Error) => void;
    editorRef?: Ref<LexicalEditor>;

    error?: boolean;
    autoFocus?: boolean;
    disabled?: boolean;
    showClear?: boolean;
    onClear?: () => void;
    clearLabel?: string;
    className?: string;
    contentClassName?: string;

    /**
     * Extra plugins (toolbar, etc.) rendered inside the LexicalComposer.
     * The wrapper carries a `group` class so children can key visibility off
     * `group-focus-within:*` variants.
     */
    children?: ReactNode;
};

export function RichTextInput({
    value,
    onChange,
    placeholder,
    theme,
    nodes,
    namespace = 'RichTextInput',
    onError = () => {},
    editorRef,
    error = false,
    autoFocus = false,
    disabled = false,
    showClear = false,
    onClear,
    clearLabel,
    className,
    contentClassName,
    children,
}: RichTextInputProps): ReactElement {
    // The editable is only as tall as the text it holds, so the field's reserved
    // toolbar band looks like the input but is not it — a click there would blur the
    // field rather than focus it. Any click that is neither in the text nor on a
    // control gets handed to the editor, caret at the end.
    //
    // Needs no editor instance, only the DOM, so it stays out of the plugin tree.
    const focusEditable = (event: MouseEvent<HTMLDivElement>): void => {
        if (!(event.target instanceof Element)) {
            return;
        }
        // Clicks in the text place the caret where the user pointed, and clicks on a
        // control are that control's to handle.
        if (event.target.closest('[contenteditable], button, a, input, textarea')) {
            return;
        }

        event.preventDefault();

        const editable = event.currentTarget.querySelector<HTMLElement>('[contenteditable="true"]');
        // Already focused: the click keeps whatever selection the user had.
        if (!editable || editable.contains(document.activeElement)) {
            return;
        }
        editable.focus();

        // Clicking below the text means "put me after it", so collapse to the end —
        // a bare `focus()` lands the caret at the start. Lexical adopts this through
        // the usual selectionchange, so no editor API is needed.
        const range = document.createRange();
        range.selectNodeContents(editable);
        range.collapse(false);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
    };

    const initialConfig = {
        namespace,
        nodes,
        editorState: value ? JSON.stringify(value) : undefined,
        theme,
        onError,
        editable: !disabled,
    };

    return (
        <div
            aria-disabled={disabled || undefined}
            onMouseDown={focusEditable}
            className={cn(
                'group w-full bg-container relative border',
                'rounded-default border-line-weak not-focus-within:hover:border-line-strong focus-within:border-brand',
                error && 'border-danger focus-within:border-danger',
                'state-disabled:pointer-events-none state-disabled:bg-container-disabled',
                'state-disabled:border-line-weak state-disabled:text-content-disabled',
                'not-focus-within:not-has-aria-expanded:*:data-[slot=rich-text-toolbar]:invisible',
                className,
            )}
        >
            <LexicalComposer initialConfig={initialConfig}>
                <RichTextPlugin
                    contentEditable={
                        <ContentEditable
                            className={cn(
                                'relative overflow-auto px-default py-cozy text-ui-lg',
                                'text-content outline-hidden sm:text-ui-base',
                                'state-disabled:text-content-disabled',
                                showClear && 'pr-relaxed',
                                contentClassName,
                            )}
                            aria-placeholder={placeholder ?? ''}
                            placeholder={
                                // Lexical renders this as a sibling of the editable,
                                // so it positions against the wrapper and repeats the
                                // editable's padding rather than offsetting by it.
                                <span
                                    className={cn(
                                        'absolute inset-0 pointer-events-none',
                                        'px-default py-cozy text-ui-base text-content-weaker',
                                        showClear && 'pr-relaxed',
                                    )}
                                >
                                    {placeholder}
                                </span>
                            }
                            style={{ userSelect: 'text' }}
                        />
                    }
                    ErrorBoundary={LexicalErrorBoundary}
                />
                <ListPlugin />
                <LinkPlugin />
                <TablePlugin />
                <HistoryPlugin />
                <MarkdownShortcutPlugin transformers={DEFAULT_TRANSFORMERS} />
                <AutoLinkPlugin matchers={DEFAULT_LINK_MATCHERS} />
                {onChange && (
                    <OnChangePlugin
                        ignoreSelectionChange
                        onChange={(editorState, _, tags) => {
                            if (tags.has(internalRichTextInputStateUpdateTag)) {
                                return;
                            }

                            onChange(editorState.toJSON());
                        }}
                    />
                )}
                {editorRef && <EditorRefPlugin editorRef={editorRef} />}
                <ValueSyncPlugin value={value} />
                <EditableSyncPlugin editable={!disabled} />
                {autoFocus && <AutoFocusPlugin />}
                {showClear && <ClearButton label={clearLabel} onClear={onClear} />}
                <RichTextFormattingProvider>{children}</RichTextFormattingProvider>
            </LexicalComposer>
        </div>
    );
}
